/**
 * Love Garden Game Engine
 * Core match-3 game logic
 */

// Tile types
export const TILE_TYPES = ['heart', 'petal', 'leaf', 'droplet', 'star'];

// Special types
export const SPECIAL_TYPES = {
  LINE_CLEAR: 'line_clear',
  BOMB: 'bomb',
  RAINBOW: 'rainbow'
};

// Blocker types
export const BLOCKER_TYPES = {
  WEED: 'weed',
  STONE: 'stone',
  VINE: 'vine',
  FROZEN: 'frozen',
  SEED_CRATE: 'seed_crate'
};

/**
 * Check if two tiles are adjacent (horizontally or vertically)
 */
export function areAdjacent(row1, col1, row2, col2) {
  return (
    (Math.abs(row1 - row2) === 1 && col1 === col2) ||
    (Math.abs(col1 - col2) === 1 && row1 === row2)
  );
}

/**
 * Find all matches on the board (3 or more in a row/column)
 * Returns array of match objects with positions and type
 */
export function findMatches(board) {
  if (!board || !Array.isArray(board) || board.length === 0) {
    console.log('findMatches: Invalid board');
    return [];
  }

  const matches = [];
  const matched = new Set();
  console.log('findMatches: Starting match detection on 8x8 board');
  
  // Log board state for debugging
  console.log('Board state:', board.map((row, r) => 
    row.map((tile, c) => `[${r},${c}]:${tile?.type || 'null'}`).join(' ')
  ).join('\n'));

  // Check horizontal matches
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col <= 5; col++) { // Fixed: col <= 5 to check all starting positions (0-5 can have matches extending to 7)
      const tile = board[row][col];
      if (!tile || tile.type === 'empty' || tile.blocker === 'frozen' || matched.has(`${row},${col}`)) continue;

      let matchLength = 1;
      let matchType = tile.type;

      // Count consecutive tiles of same type
      while (col + matchLength < 8 && 
             board[row][col + matchLength] && 
             board[row][col + matchLength].type === matchType &&
             board[row][col + matchLength].blocker !== 'frozen') {
        matchLength++;
      }

      if (matchLength >= 3) {
        console.log(`Found horizontal match: row=${row}, startCol=${col}, length=${matchLength}, type=${matchType}`);
        const positions = [];
        for (let i = 0; i < matchLength; i++) {
          positions.push({ row, col: col + i });
          matched.add(`${row},${col + i}`);
        }
        matches.push({
          type: matchType,
          positions,
          direction: 'horizontal',
          length: matchLength
        });
      }
    }
  }

  // Check vertical matches
  for (let col = 0; col < 8; col++) {
    for (let row = 0; row <= 5; row++) { // Fixed: row <= 5 to check all starting positions (0-5 can have matches extending to 7)
      const tile = board[row][col];
      if (!tile || tile.type === 'empty' || tile.blocker === 'frozen' || matched.has(`${row},${col}`)) continue;

      let matchLength = 1;
      let matchType = tile.type;

      while (row + matchLength < 8 && 
             board[row + matchLength][col] && 
             board[row + matchLength][col].type === matchType &&
             board[row + matchLength][col].blocker !== 'frozen') {
        matchLength++;
      }

      if (matchLength >= 3) {
        console.log(`Found vertical match: col=${col}, startRow=${row}, length=${matchLength}, type=${matchType}`);
        const positions = [];
        for (let i = 0; i < matchLength; i++) {
          positions.push({ row: row + i, col });
          matched.add(`${row + i},${col}`);
        }
        matches.push({
          type: matchType,
          positions,
          direction: 'vertical',
          length: matchLength
        });
      }
    }
  }

  console.log(`findMatches: Found ${matches.length} matches`, matches.map(m => ({
    type: m.type,
    direction: m.direction,
    length: m.length,
    positions: m.positions
  })));
  return matches;
}

/**
 * Detect if a match creates a special piece
 * Returns special type or null
 */
export function detectSpecialCreation(match) {
  const { length, direction, positions } = match;

  // Rainbow Heart: Match-5 in a line
  if (length === 5) {
    return {
      type: SPECIAL_TYPES.RAINBOW,
      position: positions[Math.floor(length / 2)]
    };
  }

  // Bomb: Match-5 in L or T shape (check surrounding tiles)
  if (length === 4) {
    // Check for L/T shape
    const isLOrT = checkLOrTShape(positions);
    if (isLOrT) {
      return {
        type: SPECIAL_TYPES.BOMB,
        position: positions[Math.floor(length / 2)]
      };
    }
  }

  // Line Clear: Match-4
  if (length === 4) {
    return {
      type: SPECIAL_TYPES.LINE_CLEAR,
      position: positions[Math.floor(length / 2)],
      direction: direction
    };
  }

  return null;
}

/**
 * Check if positions form an L or T shape
 */
function checkLOrTShape(positions) {
  // Simplified check - would need more complex logic for full implementation
  return false; // For now, require manual detection
}

/**
 * Apply gravity - tiles fall down to fill empty spaces
 * Returns the updated board with new tiles spawned
 */
export function applyGravity(board) {
  const newBoard = JSON.parse(JSON.stringify(board));
  const TILE_TYPES = [
    'heart', 'flower', 'butterfly',  // Heart's Awakening
    'lotus', 'droplet', 'feather',  // Inner Peace
    'star', 'moon', 'cloud',  // Starlit Dreams
    'gem', 'lightray', 'bluegem',  // Crystal Clarity
    'leaf', 'petal',  // Transformation Garden
    'rose', 'sparkle', 'sun'  // Eternal Bloom
  ];

  // Process each column from bottom to top
  for (let col = 0; col < 8; col++) {
    // Collect all non-empty tiles in this column
    const tiles = [];
    for (let row = 0; row < 8; row++) {
      const tile = newBoard[row][col];
      if (tile && tile.type !== 'empty' && !tile.blocker) {
        tiles.push({ ...tile });
      }
    }
    
    // Fill from bottom with existing tiles
    let tileIndex = tiles.length - 1;
    for (let row = 7; row >= 0; row--) {
      if (newBoard[row][col].blocker) {
        // Keep blockers in place
        continue;
      }
      
      if (tileIndex >= 0) {
        // Use existing tile
        newBoard[row][col] = {
          ...tiles[tileIndex],
          row,
          col
        };
        tileIndex--;
      } else {
        // Spawn new tile
        newBoard[row][col] = {
          type: TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)],
          row,
          col,
          blocker: null,
          blocker_health: 0,
          is_special: false,
          special_type: null
        };
      }
    }
  }

  return newBoard;
}

/**
 * Check if a swap would create a special piece
 */
export function checkSpecialMatch(board, row1, col1, row2, col2) {
  // Temporarily swap
  const temp = board[row1][col1];
  board[row1][col1] = board[row2][col2];
  board[row2][col2] = temp;
  
  // Check for matches
  const matches = findMatches(board);
  
  // Swap back
  board[row2][col2] = board[row1][col1];
  board[row1][col1] = temp;
  
  return matches.length > 0;
}

/**
 * Fill empty spaces with new random tiles
 */
export function fillEmptySpaces(board) {
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (!board[row][col] || board[row][col].type === 'empty') {
        board[row][col] = {
          type: TILE_TYPES[Math.floor(Math.random() * TILE_TYPES.length)],
          row,
          col,
          blocker: null,
          blocker_health: 0,
          is_special: false
        };
      }
    }
  }
}

/**
 * Remove matches from board and damage adjacent blockers
 */
export function removeMatches(board, matches) {
  const removed = [];
  const damagedBlockers = [];

  matches.forEach(match => {
    match.positions.forEach(({ row, col }) => {
      const tile = board[row][col];
      if (tile) {
        removed.push({ row, col, type: tile.type });
        
        // Damage adjacent blockers
        const adjacent = getAdjacentPositions(row, col);
        adjacent.forEach(({ r, c }) => {
          if (board[r] && board[r][c] && board[r][c].blocker) {
            const blocker = board[r][c];
            blocker.blocker_health = Math.max(0, blocker.blocker_health - 1);
            
            if (blocker.blocker_health === 0) {
              blocker.blocker = null;
              damagedBlockers.push({ row: r, col: c, type: blocker.blocker });
            }
          }
        });

        // Clear tile
        board[row][col] = { type: 'empty', row, col };
      }
    });
  });

  return { removed, damagedBlockers };
}

/**
 * Get adjacent positions (up, down, left, right)
 */
function getAdjacentPositions(row, col) {
  const positions = [];
  if (row > 0) positions.push({ r: row - 1, c: col });
  if (row < 7) positions.push({ r: row + 1, c: col });
  if (col > 0) positions.push({ r: row, c: col - 1 });
  if (col < 7) positions.push({ r: row, c: col + 1 });
  return positions;
}

/**
 * Check if board has any possible moves
 */
export function hasPossibleMoves(board) {
  // Check all possible swaps
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      // Try swapping with right neighbor
      if (col < 7) {
        const testBoard = swapTiles(board, row, col, row, col + 1);
        if (findMatches(testBoard).length > 0) {
          return true;
        }
      }
      
      // Try swapping with bottom neighbor
      if (row < 7) {
        const testBoard = swapTiles(board, row, col, row + 1, col);
        if (findMatches(testBoard).length > 0) {
          return true;
        }
      }
    }
  }
  
  return false;
}

/**
 * Swap two tiles and return new board
 */
function swapTiles(board, row1, col1, row2, col2) {
  const newBoard = board.map(row => row.map(tile => ({ ...tile })));
  const temp = newBoard[row1][col1];
  newBoard[row1][col1] = { ...newBoard[row2][col2], row: row1, col: col1 };
  newBoard[row2][col2] = { ...temp, row: row2, col: col2 };
  return newBoard;
}

/**
 * Calculate score for a match
 */
export function calculateMatchScore(match, comboMultiplier = 1) {
  const baseScore = match.positions.length * 10;
  return Math.floor(baseScore * comboMultiplier);
}

/**
 * Activate special piece effect
 */
export function activateSpecial(board, row, col, specialType, targetType = null) {
  const tilesToClear = [];

  switch (specialType) {
    case SPECIAL_TYPES.LINE_CLEAR:
      // Clear entire row or column
      const direction = board[row][col].special_direction || 'horizontal';
      if (direction === 'horizontal') {
        for (let c = 0; c < 8; c++) {
          tilesToClear.push({ row, col: c });
        }
      } else {
        for (let r = 0; r < 8; r++) {
          tilesToClear.push({ row: r, col });
        }
      }
      break;

    case SPECIAL_TYPES.BOMB:
      // Clear 3x3 area
      for (let r = Math.max(0, row - 1); r <= Math.min(7, row + 1); r++) {
        for (let c = Math.max(0, col - 1); c <= Math.min(7, col + 1); c++) {
          tilesToClear.push({ row: r, col: c });
        }
      }
      break;

    case SPECIAL_TYPES.RAINBOW:
      // Clear all tiles of target type
      if (targetType) {
        for (let r = 0; r < 8; r++) {
          for (let c = 0; c < 8; c++) {
            if (board[r][c] && board[r][c].type === targetType) {
              tilesToClear.push({ row: r, col: c });
            }
          }
        }
      }
      break;
  }

  return tilesToClear;
}